import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { devLog } from "../lib/devLog";
import { usePolling } from "../lib/usePolling";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import { STATUS_LABEL, roleLabel } from "../lib/labels";
import { useAuth } from "../contexts/AuthContext";
import { DownloadSimple } from "@phosphor-icons/react";

const STATUS_COLORS = {
  todo: "#71717A",
  doing: "#007AFF",
  with_editor: "#A855F7",
  to_be_checked: "#F59E0B",
  checked: "#10B981",
  done: "#22C55E",
};

function formatDuration(seconds) {
  if (!seconds) return "0m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const RANGE_PRESETS = [
  { id: "today", label: "Today", days: 0 },
  { id: "all", label: "All time", days: null },
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
  { id: "custom", label: "Custom range", days: null },
];

function toISODate(d) {
  return d.toISOString().slice(0, 10);
}

function presetToRange(presetId, customStart, customEnd) {
  if (presetId === "all") return { start: null, end: null };
  if (presetId === "custom") {
    return {
      start: customStart ? new Date(customStart + "T00:00:00.000Z").toISOString() : null,
      end: customEnd ? new Date(customEnd + "T23:59:59.999Z").toISOString() : null,
    };
  }
  if (presetId === "today") {
    const end = new Date();
    const start = new Date();
    start.setHours(0, 0, 0, 0); // local midnight
    return { start: start.toISOString(), end: end.toISOString() };
  }
  const preset = RANGE_PRESETS.find((p) => p.id === presetId);
  if (!preset?.days) return { start: null, end: null };
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - preset.days);
  return { start: start.toISOString(), end: end.toISOString() };
}

export function AnalyticsTab({ projectId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [peerTab, setPeerTab] = useState("workers");
  const [preset, setPreset] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { start, end } = presetToRange(preset, customStart, customEnd);
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get(`/projects/${projectId}/analytics`, { params });
      setData(res.data);
    } catch (e) {
      devLog.error("[Analytics] load failed", e);
      /* keep last good data on transient failures */
    }
    if (!silent) setLoading(false);
  }, [projectId, preset, customStart, customEnd]);

  useEffect(() => { load(); }, [load]);

  // Background refresh — no spinner flicker
  usePolling(() => load(true), 15000);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { start, end } = presetToRange(preset, customStart, customEnd);
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get(`/projects/${projectId}/analytics/export`, {
        params,
        responseType: "blob",
      });
      // Extract filename from Content-Disposition if provided
      const disposition = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const filename = match ? match[1] : `analytics_${projectId}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      devLog.error("[Analytics] export failed", e);
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) return <p className="text-[color:var(--text-tertiary)]">Loading analytics...</p>;
  if (!data) return <p className="text-[color:var(--text-tertiary)]">No data yet.</p>;

  const statusData = Object.entries(data.totals.by_status).map(([k, v]) => ({
    name: STATUS_LABEL[k] || k.replace(/_/g, " "),
    value: v,
    fill: STATUS_COLORS[k],
  }));

  const userBarData = data.user_stats.map((u) => ({
    name: u.name.split(" ")[0],
    role: u.role,
    Assigned: u.tasks_assigned_by,
    Checked: u.tasks_checked,
    Edited: u.tasks_edited || 0,
    Completed: u.tasks_completed_as_worker,
    GFX: u.tasks_gfx || 0,
    Published: u.tasks_published || 0,
  }));

  const PEER_TABS = [
    {
      id: "workers",
      label: "Producers",
      filter: (u) => u.Completed > 0 || u.role === "worker",
      metricKey: "Completed",
      metricColor: "#22C55E",
      empty: "No producer activity yet.",
    },
    {
      id: "video_editors",
      label: "Video Editors",
      filter: (u) => u.Edited > 0 || u.role === "video_editor",
      metricKey: "Edited",
      metricColor: "#EC4899",
      empty: "No video editor activity yet.",
    },
    {
      id: "graphic_designers",
      label: "Graphic Designers",
      filter: (u) => u.GFX > 0 || u.role === "graphic_designer",
      metricKey: "GFX",
      metricColor: "#38BDF8",
      empty: "No graphic designer activity yet.",
    },
    {
      id: "checkers",
      label: "Copy Editors",
      filter: (u) => u.Checked > 0 || u.role === "checker",
      metricKey: "Checked",
      metricColor: "#F59E0B",
      empty: "No copy editor activity yet.",
    },
    {
      id: "publishers",
      label: "Publishers",
      filter: (u) => u.Published > 0 || u.role === "publisher",
      metricKey: "Published",
      metricColor: "#F472B6",
      empty: "No publisher activity yet.",
    },
    {
      id: "assigners",
      label: "Assigners",
      filter: (u) => u.Assigned > 0 || u.role === "assigner" || u.role === "admin",
      metricKey: "Assigned",
      metricColor: "#007AFF",
      empty: "No assigner activity yet.",
    },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="surface p-4 rounded-md flex flex-wrap items-center gap-3" data-testid="analytics-range-bar">
        <p className="label-mono">Time period</p>
        <div className="flex gap-1 bg-[color:var(--surface-2)] rounded-md p-1 border border-[color:var(--border)] flex-wrap">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.id}
              data-testid={`range-preset-${p.id}`}
              onClick={() => setPreset(p.id)}
              className={`px-3 py-1 text-[11px] font-heading font-bold uppercase tracking-wider rounded transition ${
                preset === p.id
                  ? "bg-[color:var(--bg)] text-white"
                  : "text-[color:var(--text-tertiary)] hover:text-white"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-2 ml-auto" data-testid="custom-range">
            <input
              data-testid="custom-range-start"
              type="date"
              value={customStart}
              max={customEnd || toISODate(new Date())}
              onChange={(e) => setCustomStart(e.target.value)}
              className="input"
              style={{ padding: "8px 12px", width: "auto" }}
            />
            <span className="text-[color:var(--text-tertiary)]">→</span>
            <input
              data-testid="custom-range-end"
              type="date"
              value={customEnd}
              min={customStart}
              max={toISODate(new Date())}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="input"
              style={{ padding: "8px 12px", width: "auto" }}
            />
          </div>
        )}
        {loading && <span className="text-xs text-[color:var(--text-tertiary)] ml-auto font-mono">refreshing…</span>}
        {user?.role === "admin" && (
          <button
            data-testid="export-analytics-btn"
            onClick={handleExport}
            disabled={exporting}
            className={`${loading ? "" : "ml-auto"} inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[color:var(--border)] bg-[color:var(--surface-2)] hover:bg-[color:var(--bg)] text-white text-[11px] font-heading font-bold uppercase tracking-wider transition disabled:opacity-50 disabled:cursor-not-allowed`}
            title="Download this project's analytics as an Excel file"
          >
            <DownloadSimple size={14} weight="bold" />
            {exporting ? "Exporting…" : "Export Excel"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total tasks" value={data.totals.total_tasks} accent="#FFFFFF" />
        {Object.entries(data.totals.by_status).map(([k, v]) => (
          <StatCard key={k} label={STATUS_LABEL[k] || k.replace(/_/g, " ")} value={v} accent={STATUS_COLORS[k]} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="surface p-6 rounded-md">
          <p className="label-mono mb-1">Distribution</p>
          <h3 className="font-heading text-lg font-bold mb-4">Tasks by status</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {statusData.map((s) => <Cell key={s.name} fill={s.fill} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0E0E10", border: "1px solid #27272A", borderRadius: 6 }} />
              <Legend wrapperStyle={{ fontFamily: "Manrope", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="surface p-6 rounded-md">
          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
            <p className="label-mono">Team performance</p>
            <div className="flex gap-1 bg-[color:var(--surface-2)] rounded-md p-1 border border-[color:var(--border)]">
              {PEER_TABS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`peer-tab-${t.id}`}
                  onClick={() => setPeerTab(t.id)}
                  className={`px-3 py-1 text-[11px] font-heading font-bold uppercase tracking-wider rounded transition ${
                    peerTab === t.id
                      ? "bg-[color:var(--bg)] text-white"
                      : "text-[color:var(--text-tertiary)] hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {PEER_TABS.filter((t) => t.id === peerTab).map((t) => {
            const filtered = userBarData.filter(t.filter);
            return (
              <div key={t.id}>
                <h3 className="font-heading text-lg font-bold mb-4">{t.label} · {t.metricKey.toLowerCase()} per peer</h3>
                {filtered.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={filtered}>
                      <CartesianGrid stroke="#27272A" vertical={false} />
                      <XAxis dataKey="name" stroke="#A1A1AA" fontSize={11} />
                      <YAxis stroke="#A1A1AA" fontSize={11} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "#0E0E10", border: "1px solid #27272A", borderRadius: 6 }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                      <Legend wrapperStyle={{ fontFamily: "Manrope", fontSize: 12 }} />
                      <Bar dataKey={t.metricKey} fill={t.metricColor} radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-[color:var(--text-tertiary)] py-12 text-center">{t.empty}</p>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="surface rounded-md overflow-hidden">
        <div className="px-6 py-4 border-b border-[color:var(--border)]">
          <p className="label-mono mb-1">Detailed</p>
          <h3 className="font-heading text-lg font-bold">Member breakdown</h3>
        </div>
        <div className="grid grid-cols-12 px-6 py-3 border-b border-[color:var(--border)] bg-[color:var(--surface-2)] text-xs">
          <div className="col-span-3 label-mono">Operator</div>
          <div className="col-span-2 label-mono">Role</div>
          <div className="col-span-1 label-mono text-right">Assigned</div>
          <div className="col-span-1 label-mono text-right">Edited</div>
          <div className="col-span-1 label-mono text-right">GFX</div>
          <div className="col-span-1 label-mono text-right">Checked</div>
          <div className="col-span-1 label-mono text-right">Published</div>
          <div className="col-span-2 label-mono text-right">Avg time</div>
        </div>
        {data.user_stats.map((u) => {
          // Show the average time for the user's primary role
          let avgSeconds = 0;
          if (u.role === "video_editor") avgSeconds = u.avg_edit_seconds || 0;
          else if (u.role === "checker") avgSeconds = u.avg_check_seconds || 0;
          else if (u.role === "graphic_designer") avgSeconds = u.avg_gfx_seconds || 0;
          else if (u.role === "publisher") avgSeconds = u.avg_publish_seconds || 0;
          else avgSeconds = u.avg_work_seconds || 0;
          const publishedCount = u.role === "publisher" ? (u.tasks_published || 0) : u.tasks_completed_as_worker;
          return (
            <div key={u.user_id} className="grid grid-cols-12 px-6 py-3 border-b border-[color:var(--border)] last:border-0 text-sm items-center" data-testid={`stats-row-${u.user_id}`}>
              <div className="col-span-3 font-semibold">{u.name}</div>
              <div className="col-span-2"><span className={`role-badge role-${u.role}`}>{roleLabel(u.role)}</span></div>
              <div className="col-span-1 text-right font-mono">{u.tasks_assigned_by}</div>
              <div className="col-span-1 text-right font-mono">{u.tasks_edited || 0}</div>
              <div className="col-span-1 text-right font-mono">{u.tasks_gfx || 0}</div>
              <div className="col-span-1 text-right font-mono">{u.tasks_checked}</div>
              <div className="col-span-1 text-right font-mono">{publishedCount}</div>
              <div className="col-span-2 text-right font-mono text-[color:var(--text-secondary)]">{formatDuration(avgSeconds)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="surface p-4 rounded-md">
      <p className="label-mono text-[10px]" style={{ color: accent }}>{label}</p>
      <p className="font-heading text-3xl font-black tracking-tighter mt-1">{value}</p>
    </div>
  );
}
